import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { AnimatePresence } from 'framer-motion'
import { s3Static } from '../../../../utils/constants'
import { Services } from '../../service/Services'
import { RequestProvider } from '../../../../context/RequestContext'
import { Faq } from '../../../commons/Faq'
import { buildDomesticSingaporeFaqs } from '../../../../utils/faq'
import { openModal } from '../../../../service/modal-service'
import { googleRating, googleReviewCount } from 'utils/business-info'
import { helperOneOffRows, helperRateSummary } from 'js/part-time-helper-cost'
import { HELPER_SCOPE, buildHelperDeals } from 'data/part-time-helper-deals'
import { helperBookingSteps } from 'data/service-hub-content'
import { PartTimeHelperBooking } from './PartTimeHelperBooking'
import { HelperServiceScope } from './HelperServiceScope'
import { HelperWhyChooseUs } from './HelperWhyChooseUs'
import { PartTimeHelperServices } from './PartTimeHelperServices'
import { SecurePayment } from '../SecurePayment'
import LazyYouTubeEmbed from '@/commons/LazyYouTubeEmbed'
import { PriceList } from '@/commons/service-hub/PriceList'
import { HowToBook } from '@/commons/service-hub/HowToBook'
import { ReviewsSection } from '@/commons/service-hub/ReviewsSection'
import { RewardsSection } from '@/commons/service-hub/RewardsSection'
import { AreasServed, RelatedLinks } from '@/commons/service-hub/RelatedLinks'
import { StickyCta } from '@/commons/service-hub/StickyCta'

const HUB = '/sg/part-time-cleaner-singapore'

// The four task groups a helper covers, each linking to its guide.
// Sub-headings follow HELPER_SCOPE; the copy is written for readers, not as the checklist.
const SCOPE_CARDS = [
    {
        title: 'General Cleaning',
        image: 'blog/living/part-time-helper/part-time-cleaner-with-bucket.jpg',
        imagePosition: 'bottom',
        alt: 'Part-time cleaner with a bucket and mop in a Singapore home',
        href: `${HUB}/the-role-of-part-time-cleaners-in-general-cleaning`,
        items: [
            'Dusting of surfaces, furniture, shelves and electronics.',
            'Sweeping or vacuuming of floors, carpets and rugs.',
            'Mopping of hard floors.',
            'Wiping of tabletops and countertops.',
        ],
    },
    {
        title: 'Kitchen Cleaning',
        image: 'part-time-helper/part-time-cleaner-cleaning-kitchen-countertop.jpg',
        alt: 'Part-time maid cleaning a kitchen countertop',
        href: `${HUB}/affordable-part-time-cleaners-for-kitchen-cleaning-at-20-hr`,
        items: [
            'Cleaning of kitchen countertops to remove food residue.',
            'Wiping down accessible appliances such as the microwave, stove, hood and fridge exterior.',
            'Washing and drying the dishes.',
        ],
    },
    {
        title: 'Bathroom Cleaning',
        image: 'blog/living/part-time-helper/part-time-helper-cleaning-toilet.jpeg',
        alt: 'Part-time helper cleaning a toilet',
        href: `${HUB}/affordable-part-time-cleaner-for-bathroom-cleaning`,
        items: [
            'Cleaning the sink, countertop, mirrors and glass.',
            'Cleaning taps, showerheads and the toilet bowl.',
            'Washing the bathroom floor and walls.',
        ],
    },
    {
        title: 'Bedrooms, Windows & Laundry',
        image: 'part-time-helper/chinese-helper-makin-bed.jpg',
        alt: 'Part-time helper making a bed',
        items: [
            'Changing the bed sheets.',
            'Wiping the accessible indoor side of windows.',
            'Wiping standing or table fans (ceiling fans are out of scope).',
            'Most helpers can also iron and fold clothes.',
        ],
    },
]

const COMPARISON = [
    ['How they work', 'Visits of a few hours: one-off, weekly or fortnightly', 'Lives in your home and works daily'],
    ['Work permit and levy', 'Not needed', 'MOM work permit and a monthly levy'],
    ['Housing and food', 'Not needed', 'You provide accommodation and food'],
    ['What you pay', 'Only the hours you book', 'Monthly salary, plus levy, insurance and agency fees'],
    ['Commitment', 'No long-term contract', 'Usually a two-year contract'],
    ['Best for', 'Keeping a home clean without a live-in helper', 'Families who need daily childcare or eldercare'],
]

const relatedLinks = [
    { href: `${HUB}/part-time-cleaner-rates-singapore`, label: 'Part Time Cleaner Rates', desc: 'What part-time cleaners cost in Singapore' },
    { href: `${HUB}/job-scope-of-part-time-cleaner-service`, label: 'Part Time Helper Job Scope', desc: 'What is and is not included' },
    { href: `${HUB}/how-stringssg-works`, label: 'How StringsSG Helpers Work', desc: 'Vetting, standards and our guarantee' },
    { href: `${HUB}/professional-house-cleaning`, label: 'Professional House Cleaning', desc: 'Deep cleaning by a team' },
    { href: `${HUB}/move-in-move-out-cleaning`, label: 'Move-in Move-out Cleaning', desc: 'End-of-tenancy cleaning' },
    { href: `${HUB}/spring-cleaning`, label: 'Spring Cleaning', desc: 'Top-to-bottom home cleaning' },
    { href: `${HUB}/elderly-care`, label: 'Part Time Elderly Care', desc: 'Hourly care for the elderly' },
    { href: `${HUB}/part-time-helper-for-cooking`, label: 'Part Time Helper for Cooking', desc: 'Meal prep and simple cooking' },
    { href: `${HUB}/part-time-babysitter-singapore`, label: 'Part Time Babysitter', desc: 'Hourly childcare' },
    { href: '/sg/blogs/house-keeping', label: 'Housekeeping Guides', desc: 'Cleaning tips for Singapore homes' },
    { href: `${HUB}/part-time-cleaner-jobs`, label: 'Part Time Cleaner Jobs', desc: 'Join StringsSG as a helper' },
]

export const PartTimeHelper = (props) => {
    const {
        requestCategorySettings,
        countryName,
        currencyCd,
        helperService,
        elderlyCareService,
        testimonials,
        month,
        year,
    } = props
    const requestType = 'Domestic Services'

    const [ requestForm, setRequestForm ] = useState(false)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ openDeal, setOpenDeal ] = useState(null)

    const { fromPerHour, fromTotal, minHours, maxHours } = helperRateSummary(helperService)
    const oneOff = helperRateSummary(helperOneOffRows())
    const deals = buildHelperDeals(helperService)
    const faqs = buildDomesticSingaporeFaqs({ fromPerHour, fromTotal, minHours, maxHours, oneOffFromPerHour: oneOff.fromPerHour })

    // Weekly/fortnightly and one-off are priced differently by the backend, so
    // the table shows both instead of implying one price for every frequency.
    const toRows = (rows, category) => rows.map((row) => ({
        itemName: `${row.unit} hours`,
        subCategory: `$${(Number(row.rate) / row.unit).toFixed(2)} per hour`,
        category,
        serviceCost: Number(row.rate),
    }))
    const priceRows = [
        ...toRows(helperService, 'Weekly or Fortnightly, Price Per Visit'),
        ...toRows(helperOneOffRows(), 'One-Off Visit, Price'),
    ]

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

  return (
    <>
        <RequestProvider>
            <section id="part-time-cleaner-singapore" className='relative'>
                {/* Static image on every screen size. Mobile used to autoplay a
                    video here, which was the page's LCP element. */}
                <div className="-mt-14 relative w-full aspect-video sm:aspect-auto sm:h-180 overflow-hidden">
                    <Image
                        src={s3Static + "part-time-helper/part-time-helper-in-gray-uniform-living.jpg"}
                        fill
                        className="object-cover"
                        alt="Part-time helper cleaning a living room in Singapore"
                        priority
                        sizes="100vw"
                    />
                </div>
                <div id="book" className='relative bg-gray-100 sm:bg-transparent sm:text-white w-full py-2 sm:absolute sm:top-1/2 sm:-translate-y-1/2 scroll-mt-20'>
                    <PartTimeHelperBooking requestCategorySettings={requestCategorySettings} helperService={helperService} elderlyCareService={elderlyCareService}
                        setRequestForm={setRequestForm} setIsQuickBook={setIsQuickBook} countryName={countryName} currencyCd={currencyCd} helperType="Helper" setIsShowJobScope={setIsShowJobScope}
                        title="Part Time Cleaner & Helper in Singapore"
                        subtitle={`Part-time maids from $${fromPerHour}/hr. Book ${minHours} to ${maxHours} hours, one-off, weekly or fortnightly.`}
                        pills={['Same helper every visit', 'Monday to Saturday']}/>
                </div>
            </section>
            {requestForm &&
                <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook} asSheet/>
            }
        </RequestProvider>

        <PriceList
            id="rates"
            items={priceRows}
            title="Part Time Cleaner Rates in Singapore"
            subtitle={`One helper, ${minHours} to ${maxHours} hours per visit. Weekly and fortnightly visits cost less than a one-off, and the longer the visit, the lower the hourly rate. Saturday visits cost slightly more. You see the exact price before you confirm.`}
            moreHref={`${HUB}/part-time-cleaner-rates-singapore`}
            moreLabel="Compare part-time cleaner rates in Singapore"
        />

        <section className='max-w-6xl mx-auto py-8 px-2'>
            <div className='grid md:grid-cols-3 gap-5 items-center'>
                <header className='md:col-span-2 space-y-5 px-2'>
                    <h2 className='text-2xl md:text-3xl text-center'>
                        <strong>Trusted Part Time Maid Service in Singapore</strong>
                    </h2>
                    <div className='space-y-4 text-sm md:text-base leading-relaxed text-gray-700'>
                        <p>
                            <strong>StringsSG</strong> sends part-time helpers to keep Singapore homes clean, from a one-off
                            visit before guests arrive to a weekly or fortnightly cleaner who knows your home. You choose
                            {` ${minHours} to ${maxHours}`} hours, and the helper covers dusting, floors, the kitchen, bathrooms,
                            bed sheets and, for most helpers, ironing.
                        </p>
                        <ul className='list-disc pl-5 space-y-2'>
                            <li><strong>Background verified:</strong> every helper, from licensed, ACRA-registered agencies</li>
                            <li><strong>The same helper every visit:</strong> for weekly and fortnightly bookings</li>
                            <li><strong>Monday to Saturday:</strong> visits start any time from 8am to 5pm</li>
                            <li><strong>Rated {googleRating} on Google:</strong> from {googleReviewCount} customer reviews</li>
                            <li><strong>100% satisfaction guarantee:</strong> if you are not happy, we will make it right</li>
                        </ul>
                        <p>
                            Every booking follows the scope and standards StringsSG sets, and we take responsibility for the result.{' '}
                            <Link href={`${HUB}/how-stringssg-works`} prefetch={false} className='text-orange-600 hover:underline'>
                                How the StringsSG helper service works
                            </Link>
                        </p>
                    </div>
                </header>
                <div className='md:col-span-1 relative w-full h-96 rounded-xl hidden md:block'>
                    <Image className='rounded-xl'
                        src={s3Static + "part-time-helper/part-time-helper-sg.png"}
                        fill
                        sizes="33vw"
                        style={{objectFit: 'contain', objectPosition: 'center'}}
                        alt='Part-time domestic helper in Singapore - StringsSG'
                    />
                </div>
            </div>
        </section>

        <section id="packages" className='py-10 space-y-5 max-w-5xl mx-auto'>
            <header className='px-2 text-center space-y-2'>
                <h2 className='text-xl md:text-2xl uppercase'>
                    <strong>Part Time Cleaner Packages by Home Size - {month} {year}</strong>
                </h2>
                <p className='text-sm text-gray-600 max-w-2xl mx-auto'>
                    Suggested hours for each home size, priced per visit for weekly or fortnightly bookings. You can pick any duration from {minHours} to {maxHours} hours when you book.
                </p>
            </header>
            <ul className='md:grid sm:grid-cols-2 lg:grid-cols-3 md:gap-5 overflow-x-scroll overflow-y-hidden scrollbar-hide whitespace-nowrap px-2'>
                {deals.map((deal) => (
                    <li key={deal.title} className='border bg-slate-700 text-white inline-block align-top mr-3 md:mr-0 relative w-5/6 md:w-full h-96 overflow-hidden rounded-3xl whitespace-normal'>
                        {openDeal === deal.title ?
                            <div className="h-full flex flex-col p-4 text-sm">
                                <h3 className="text-lg font-bold">{deal.title}</h3>
                                <div className="flex-1 overflow-y-auto space-y-3 pt-2">
                                    <p className="text-amber-300">{HELPER_SCOPE.notice}</p>
                                    <ul className="text-xs italic space-y-1">
                                        {HELPER_SCOPE.disclaimers.map((text) => <li key={text}>* {text}</li>)}
                                    </ul>
                                    {HELPER_SCOPE.sections.map(({ key, title, items }) => (
                                        <div key={key}>
                                            <h4 className="uppercase font-bold">{title}</h4>
                                            <ul className="pl-4 list-disc space-y-1 pt-1">
                                                {items.map((item) => <li key={item}>{item}</li>)}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                                <div className="pt-3 flex justify-center">
                                    <button type="button" className='border border-white py-2 px-3 rounded' onClick={() => setOpenDeal(null)}>
                                        Close
                                    </button>
                                </div>
                            </div>
                            :
                            <>
                                <Image
                                    src={s3Static + deal.image}
                                    fill
                                    sizes="(max-width: 768px) 85vw, 33vw"
                                    style={{objectFit: 'cover'}}
                                    placeholder='blur'
                                    blurDataURL={s3Static + "placeholder.png"}
                                    alt={`Part-time helper cleaning a ${deal.title.toLowerCase()} home`}
                                />
                                <div className='absolute top-2 left-2 h-20 w-20 bg-linear-to-tr from-red-900 to-red-400 rounded-full flex justify-center items-center text-center text-lg leading-tight'>
                                    {Math.round(((deal.originalCost - deal.cost) / deal.originalCost) * 100)}%<br/>OFF
                                </div>
                                <div className="absolute bottom-0 inset-x-0 pt-20 bg-linear-to-b from-transparent to-black space-y-1 px-2 pb-5 text-center">
                                    <h3 className='text-xl font-bold'>{deal.title}</h3>
                                    <p className='text-xl font-bold'>
                                        {deal.unit} Hours - ${deal.cost.toFixed(2)}
                                        <span className='text-gray-300 font-normal'> / <del>${deal.originalCost.toFixed(2)}</del></span>
                                    </p>
                                    <p className='text-sm'>(${(deal.cost / deal.unit).toFixed(2)}/hr)</p>
                                    <div className='pt-2'>
                                        <button type="button" onClick={() => setOpenDeal(deal.title)} className='py-2 px-3 border border-white rounded-lg text-sm'>
                                            What&apos;s included
                                        </button>
                                    </div>
                                </div>
                            </>
                        }
                    </li>
                ))}
            </ul>
            <div className='flex justify-evenly'>
                <button type="button" className='px-8 py-3 text-lg text-white bg-linear-to-tr from-orange-600 to-amber-400 rounded-full' onClick={openRequestForm}>
                    Book Now
                </button>
            </div>
        </section>

        <section id="scope" className='max-w-5xl mx-auto my-10'>
            <header className='p-4 space-y-4'>
                <h2 className='text-center text-2xl md:text-3xl uppercase'>
                    <strong>What a Part Time Helper Does</strong>
                </h2>
                <p className="leading-8">
                    A part-time cleaner keeps your bedrooms, living room, kitchen and bathrooms clean and tidy. The helper
                    does not bring equipment, so please have your own mop, vacuum, cloths and detergents ready.{' '}
                    <Link href={`${HUB}/job-scope-of-part-time-cleaner-service`} prefetch={false} className='text-orange-600 hover:underline'>
                        See the full part-time helper job scope
                    </Link>
                </p>
            </header>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6 px-2'>
                {SCOPE_CARDS.map((card) => (
                    <article key={card.title} className='border rounded-3xl overflow-hidden'>
                        <div className='h-72 sm:h-80 relative'>
                            <Image
                                src={s3Static + card.image}
                                fill
                                sizes="(max-width: 768px) 100vw, 50vw"
                                style={{objectFit: 'cover', objectPosition: card.imagePosition || 'center'}}
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt={card.alt}
                            />
                        </div>
                        <div className="space-y-4 py-5">
                            <h3 className='text-center text-xl uppercase font-bold'>{card.title}</h3>
                            <ul className='space-y-2 leading-7 pl-6 pr-2 list-disc text-sm'>
                                {card.items.map((item) => <li key={item}>{item}</li>)}
                            </ul>
                            {card.href &&
                                <div className='py-2 text-center'>
                                    <Link className='inline-block px-5 py-3 rounded-3xl text-white font-bold bg-linear-to-tr from-orange-600 to-amber-400 hover:from-amber-400 hover:to-orange-600'
                                        href={card.href} prefetch={false}>
                                        {card.title} Guide
                                    </Link>
                                </div>
                            }
                        </div>
                    </article>
                ))}
            </div>
            <div className='mx-2 mt-6 bg-gray-50 border border-gray-200 rounded-2xl p-4 text-sm text-gray-700'>
                <h3 className='font-bold mb-2'>Not included in a part-time helper visit</h3>
                <ul className='list-disc pl-5 space-y-1'>
                    <li>Move-out, post-renovation and spring cleaning. Book{' '}
                        <Link href={`${HUB}/move-in-move-out-cleaning`} prefetch={false} className='text-orange-600 hover:underline'>move-in/move-out cleaning</Link> or{' '}
                        <Link href={`${HUB}/spring-cleaning`} prefetch={false} className='text-orange-600 hover:underline'>spring cleaning</Link> instead.</li>
                    <li>Ceiling fans, and windows that cannot be reached safely from inside.</li>
                    <li>Offices and other commercial premises. The service is for homes only.</li>
                </ul>
            </div>
            <h3 className='text-center font-bold text-lg text-gray-500 pt-10 px-2'>Video: what is included in a part-time cleaning visit</h3>
            <LazyYouTubeEmbed videoId="JmlMQDN_V3s" title="Part-time helper service in Singapore" />
        </section>

        <section className='bg-gray-100 py-10 px-4'>
            <div className='max-w-5xl mx-auto'>
                <h2 className='text-2xl md:text-3xl uppercase font-bold text-center mb-6'>
                    More Home Services From Our Helpers
                </h2>
                <PartTimeHelperServices/>
            </div>
        </section>

        <HowToBook title="How to Book a Part Time Helper" steps={helperBookingSteps}/>
        <HelperWhyChooseUs recurring/>

        <ReviewsSection
            title="What Customers Say About Our Part Time Cleaners"
            intro={<>Real feedback from homeowners who book <strong>part-time helpers and cleaners in Singapore</strong> through StringsSG.</>}
            testimonials={testimonials}
        />

        <section className='max-w-5xl mx-auto py-10 px-4'>
            <h2 className='text-2xl md:text-3xl uppercase font-bold text-center mb-3'>
                Part Time Maid vs Full-Time Domestic Helper
            </h2>
            <p className='text-sm text-gray-600 text-center max-w-2xl mx-auto mb-6 leading-relaxed'>
                A part-time maid suits most homes that need regular cleaning but not a live-in helper. Here is how the two compare.
            </p>
            <div className='overflow-x-auto rounded-2xl border border-gray-200'>
                <table className='w-full text-sm text-left'>
                    <thead className='bg-gray-900 text-white'>
                        <tr>
                            <th scope='col' className='p-3'><span className='sr-only'>Compared on</span></th>
                            <th scope='col' className='p-3'>Part-time maid (StringsSG)</th>
                            <th scope='col' className='p-3'>Full-time domestic helper</th>
                        </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-200'>
                        {COMPARISON.map(([row, partTime, fullTime]) => (
                            <tr key={row} className='bg-white'>
                                <th scope='row' className='p-3 font-bold text-gray-900 whitespace-nowrap'>{row}</th>
                                <td className='p-3 text-gray-700'>{partTime}</td>
                                <td className='p-3 text-gray-700'>{fullTime}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>

        <AreasServed
            title="Part Time Cleaner Near You, Island-Wide"
            intro="We send part-time helpers to HDB flats, condominiums and landed homes in every part of Singapore, Monday to Saturday, with visits starting any time from 8am to 5pm."
        />

        <RewardsSection serviceNoun="part-time helper"/>
        <SecurePayment/>

        <section className='max-w-5xl mx-auto text-center py-10 relative'>
            <div className='relative w-full h-120'>
                <Image
                    src={s3Static + "part-time-helper/part-time-helper-posing.jpg"}
                    fill
                    sizes="(max-width: 768px) 100vw, 1024px"
                    style={{objectFit: 'cover', objectPosition: 'top'}}
                    placeholder='blur'
                    blurDataURL={s3Static + "placeholder.png"}
                    alt='Join StringsSG as a part-time helper'
                />
            </div>
            <div className='absolute flex items-center text-white inset-0 w-fit mx-auto'>
                <Link href={`${HUB}/part-time-cleaner-jobs`} prefetch={false} className='p-4 rounded-lg bg-black/60 text-xl md:text-2xl'>
                    Join Our Team
                </Link>
            </div>
        </section>

        <section className='max-w-5xl mx-auto py-10'>
            <Faq title="Part Time Cleaner Singapore FAQs" questionsAnswers={faqs}/>
        </section>

        <RelatedLinks title="Part Time Cleaning Services, Rates and Guides" links={relatedLinks}/>

        {!requestForm &&
            <StickyCta
                bookLabel="Book a Helper"
                onBook={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                whatsAppText="Hi, I'd like to book a part-time cleaner."
            />
        }
        {isShowJobScope &&
            <AnimatePresence>
                <HelperServiceScope/>
            </AnimatePresence>
        }
    </>
  )
}
